import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuy.skin';
    const isoDate = new Date(article.content.date).toISOString().split('T')[0] || '2026-06-29';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      image: article.content.imageUrl,
      datePublished: isoDate,
      dateModified: isoDate,
      author: {
        '@type': 'Organization',
        name: 'VigorBuy Resource Team',
      },
      publisher: {
        '@type': 'Organization',
        name: 'VigorBuy.skin',
      },
      description: article.excerpt,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/article/${article.slug}`,
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-[860px] mx-auto px-6 md:px-10 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 mb-6">
              Article Not Found
            </h1>
            <p className="text-foreground-600 mb-8">
              The VigorBuy guide you are looking for does not exist or may have been moved.
            </p>
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Back to Resource Center
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { content } = article;
  const relatedArticles = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-[860px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="hover:text-foreground-950 transition-colors cursor-pointer"
              >
                Home
              </a>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-[10px]" />
              </span>
              <span>Guides</span>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-[10px]" />
              </span>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                  {content.category}
                </span>
                <span className="text-xs text-foreground-400">{content.date}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{content.readTime}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight tracking-tight mb-6">
                {article.title}
              </h1>

              <div className="rounded-2xl overflow-hidden border border-background-200/60">
                <img
                  src={content.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </header>

            <div className="space-y-12">
              {content.sections.map((section, index) => (
                <section key={index}>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                    {section.heading}
                  </h2>
                  <div className="space-y-5">
                    {section.content.split('\n\n').map((paragraph, pIndex) => (
                      <p
                        key={pIndex}
                        className="text-sm md:text-base text-foreground-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: paragraph }}
                      />
                    ))}
                  </div>

                  {index === 0 && (
                    <div className="mt-8 p-5 md:p-6 rounded-xl bg-accent-100/60 border border-accent-200/70">
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        <strong className="text-foreground-950">Key Takeaway:</strong> The{' '}
                        <a
                          href="/"
                          onClick={(e) => { e.preventDefault(); navigate('/'); }}
                          className="text-primary-600 font-semibold hover:text-primary-700 underline cursor-pointer"
                        >
                          VigorBuy Spreadsheet
                        </a>{' '}
                        is the most reliable resource for finding verified products, comparing prices, reviewing QC photos, and accessing expert shopping guides all in one place.
                      </p>
                    </div>
                  )}
                </section>
              ))}
            </div>

            <div className="mt-16 p-8 md:p-10 rounded-2xl bg-background-100 border border-background-200/70 text-center">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Back to the VigorBuy Resource Center
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                Explore more buying guides, updated spreadsheet links, and expert shopping tutorials.
              </p>
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Return to Homepage
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </a>
            </div>

            <div className="mt-16">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">
                More VigorBuy Guides &amp; Resources
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <a
                    key={ra.id}
                    href={`/article/${ra.slug}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/article/${ra.slug}`); window.scrollTo(0, 0); }}
                    className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden hover:border-accent-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-background-100">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-2">
                        {ra.tag}
                      </span>
                      <h4 className="text-sm font-bold text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}